// Renders a single chat turn as either a user question bubble or an assistant answer bubble.
// Used inside the dom-repeat loop in AIPage to display each conversation turn.

import { PolymerElement, html } from "@polymer/polymer/polymer-element.js";
import "@polymer/polymer/lib/elements/dom-if.js";
import "@polymer/polymer/lib/elements/dom-repeat.js";

import "./ChatBubble.style";
import { convertMarkdownToHtml } from "../../../../utils/ai";

class ChatBubble extends PolymerElement {
  static get template() {
    return html`
      <style include="chat-bubble-style"></style>

      <template is="dom-if" if="[[_isQuestion(turn)]]">
        <div class="chat-bubble__user-row">
            <template is="dom-if" if="[[turn.text]]">
              <div class="chat-bubble__user-text">[[turn.text]]</div>
            </template>

            <template is="dom-if" if="[[_hasQuestionImages(turn)]]">
              <div class="chat-bubble__user-images" role="list" aria-label="Lampiran gambar">
                <template is="dom-repeat" items="[[_questionImageItems(turn)]]" as="item">
                  <div class="chat-bubble__attachment-preview chat-bubble__attachment-preview--image" role="listitem">
                    <img
                      class="chat-bubble__attachment-thumb"
                      src="[[item.previewUrl]]"
                      alt="[[item.name]]"
                    />
                  </div>
                </template>
              </div>
            </template>

            <template is="dom-if" if="[[_hasQuestionFiles(turn)]]">
              <div class="chat-bubble__user-files" role="list" aria-label="Lampiran berkas">
                <template is="dom-repeat" items="[[_questionFileItems(turn)]]" as="item">
                  <div class="chat-bubble__attachment-preview chat-bubble__attachment-preview--pdf" role="listitem">
                    <span class="chat-bubble__attachment-pdf-badge" aria-hidden="true">.pdf</span>
                    <div class="chat-bubble__attachment-pdf-info">
                      <span class="chat-bubble__attachment-pdf-name">[[_truncateFileName(item.name)]]</span>
                      <span class="chat-bubble__attachment-pdf-type">PDF</span>
                    </div>
                  </div>
                </template>
              </div>
            </template>
        </div>
      </template>

      <template is="dom-if" if="[[_isAnswer(turn)]]">
        <div class="chat-bubble__assistant" inner-h-t-m-l="[[_answerHtml(turn)]]"></div>
      </template>
    `;
  }

  static get properties() {
    return {
      // Turn: { type, text, html, id, imageUrls?, fileNames? }
      turn: {
        type: Object,
        value() {
          return {};
        },
      }
    };
  }

  // Returns true if the turn is a user question.
  _isQuestion(turn) {
    return !!(turn && turn.type === 'question');
  }

  // Returns true if the turn is an assistant answer.
  _isAnswer(turn) {
    return !!(turn && turn.type === 'answer');
  }

  // Render assistant answer HTML set on the turn (e.g. from mapper or streaming).
  _answerHtml(turn) {
    if (!turn || turn.type !== "answer") return "";
    if (turn.html) return String(turn.html);
    return convertMarkdownToHtml(turn.text || "");
  }

  // Returns true when the question has image URL attachments.
  _hasQuestionImages(turn) {
    return this._questionImageItems(turn).length > 0;
  }

  // Returns true when the question has PDF file name attachments.
  _hasQuestionFiles(turn) {
    return this._questionFileItems(turn).length > 0;
  }

  // Build image preview items from API image_urls.
  _questionImageItems(turn) {
    if (!turn) return [];

    const imageUrls = Array.isArray(turn.imageUrls) ? turn.imageUrls : [];
    return imageUrls
      .filter(Boolean)
      .map((url, index) => ({
        kind: "image",
        name: `image-${index + 1}`,
        previewUrl: String(url),
      }));
  }

  // Build PDF preview items from API file_names.
  _questionFileItems(turn) {
    if (!turn) return [];

    const fileNames = Array.isArray(turn.fileNames) ? turn.fileNames : [];
    return fileNames
      .filter(Boolean)
      .map((name) => ({
        kind: "pdf",
        name: String(name),
      }));
  }

  // Shorten long file names shown on the PDF preview.
  _truncateFileName(fileName) {
    const MAX_DISPLAY_FILE_NAME_LENGTH = 19;

    const name = fileName != null ? String(fileName) : "";
    if (name.length <= MAX_DISPLAY_FILE_NAME_LENGTH) return name;
    return `${name.slice(0, MAX_DISPLAY_FILE_NAME_LENGTH)}...`;
  }
}

customElements.define("chat-bubble", ChatBubble);
